import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '@env/environment';

import {
  AdminClientRecord,
  AdminClientDetail,
  ActivationChecklistModel,
  AdminInitialImportDetailPageModel,
  AdminInitialImportStatusModel,
  AdminPmsConnectionStatusModel,
  AdminPropertyMappingRequest,
  AdminRemotePmsPropertyModel,
  AdminDashboard,
  AdminPaymentChartPoint,
  AdminPaymentRecord,
  AdminSettings,
  AdminNotificationOperationsOverview,
  AdminUserAccessStatus,
  AdminUserRecord,
  AuthResponse,
  ClientDetailModel,
  ClientProfileModel,
  ClientPropertyModel,
  ClientQueueItem,
  PartnerOnboardingStage,
  PartnerSubscriptionStatus,
  OwnerRezAdminValidationResultModel,
  OwnerRezWebhookAuthStatusModel,
  PriceLabsConnectionStatus,
  PriceLabsPropertyMapping,
} from '@app/core/models';
import {
  unwrapAdminArray,
  unwrapAdminClientDetail,
  unwrapAdminClients,
  unwrapAdminDashboard,
  unwrapAdminData,
  unwrapAdminPaymentChart,
  unwrapAdminPayments,
  unwrapAdminSettings,
  unwrapAdminUsers
} from './admin-api.utils';

export interface AdminClientUpsertPayload {
  fullName?: string;
  email?: string;
  phone?: string;
  partnerUserId?: string;
  clientName?: string;
  companyName?: string;
  contactName?: string;
  contactEmail: string;
  contactPhone: string;
  onboardingStage?: PartnerOnboardingStage;
  subscriptionStatus?: PartnerSubscriptionStatus;
  status?: string;
  password?: string;
  confirmPassword?: string;
}

export interface AdminUserUpsertPayload {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password?: string;
  confirmPassword?: string;
  accessStatus?: AdminUserAccessStatus;
}

export interface AdminPaymentQuery {
  startDate?: string;
  endDate?: string;
  status?: string;
  clientId?: string;
  paymentType?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface AdminClientQueueQuery {
  status?: string;
  search?: string;
}

export interface AdminClientPmsConnectPayload {
  providerId: string;
  displayName?: string;
  externalAccountId?: string;
  apiKey?: string;
  apiSecret?: string;
  clientId?: string;
  clientSecret?: string;
  accountId?: string;
  username?: string;
  password?: string;
  accessToken?: string;
  webhookSecret?: string;
  webhookUsername?: string;
  webhookPassword?: string;
  notes?: string;
  returnUrl?: string;
  connectionMode?: string;
}

export interface UpdateOwnerRezWebhookAuthPayload {
  webhookUsername: string;
  webhookPassword?: string;
  clientId?: string;
  clientSecret?: string;
  requireReauthorization?: boolean;
}

export interface AdminNotificationOperationsQuery {
  status?: string;
  notificationType?: string;
  take?: number;
}

export interface AdminEmailConfirmationResendResponse {
  clientId: string;
  email: string;
  sent: boolean;
  emailConfirmed: boolean;
  message: string;
}

export interface AdminEmailConfirmationLinkResponse {
  clientId: string;
  email: string;
  emailConfirmed: boolean;
  confirmationUrl: string;
  expiresAtUtc: string;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class AdminApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/admin`;

  getDashboard() {
    return this.http
      .get<unknown>(`${this.baseUrl}/dashboard`)
      .pipe(map((response) => unwrapAdminDashboard(response)));
  }

  getClients(includeDeleted = false) {
    return this.http
      .get<unknown>(`${this.baseUrl}/clients`, {
        params: includeDeleted ? new HttpParams().set('includeDeleted', 'true') : undefined
      })
      .pipe(map((response) => unwrapAdminClients(response)));
  }

  getClientQueue(query: AdminClientQueueQuery = {}) {
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });

    return this.http
      .get<unknown>(`${this.baseUrl}/clients/queue`, { params })
      .pipe(map((response) => unwrapAdminArray<ClientQueueItem>(response)));
  }

  getClient(clientId: string) {
    return this.http
      .get<unknown>(`${this.baseUrl}/clients/${clientId}`)
      .pipe(map((response) => unwrapAdminClientDetail(response)));
  }

  getClientWorkspace(clientId: string) {
    return this.http
      .get<unknown>(`${this.baseUrl}/clients/${clientId}`)
      .pipe(map((response) => unwrapAdminData<ClientDetailModel>(response)));
  }

  getClientPayments(clientId: string) {
    return this.http
      .get<unknown>(`${this.baseUrl}/clients/${clientId}/payments`)
      .pipe(map((response) => unwrapAdminPayments(response)));
  }

  updateClientProfile(clientId: string, payload: ClientProfileModel, rowVersion?: string | null) {
    return this.http
      .put<unknown>(`${this.baseUrl}/clients/${clientId}/profile`, {
        ...payload,
        rowVersion
      })
      .pipe(map((response) => unwrapAdminData<ClientProfileModel>(response)));
  }

  getActivationChecklist(clientId: string) {
    return this.http
      .get<unknown>(`${this.baseUrl}/clients/${clientId}/activation-checklist`)
      .pipe(map((response) => unwrapAdminData<ActivationChecklistModel>(response)));
  }

  startClientOnboarding(clientId: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/start-onboarding`, {})
      .pipe(map((response) => unwrapAdminData<ClientDetailModel>(response)));
  }

  createClientProperty(clientId: string, payload: Omit<ClientPropertyModel, 'id'>) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/properties`, payload)
      .pipe(map((response) => unwrapAdminData<ClientPropertyModel>(response)));
  }

  updateClientProperty(clientId: string, propertyId: string, payload: ClientPropertyModel) {
    return this.http
      .put<unknown>(`${this.baseUrl}/clients/${clientId}/properties/${propertyId}`, payload)
      .pipe(map((response) => unwrapAdminData<ClientPropertyModel>(response)));
  }

  deleteClientProperty(clientId: string, propertyId: string) {
    return this.http
      .delete<unknown>(`${this.baseUrl}/clients/${clientId}/properties/${propertyId}`)
      .pipe(map((response) => unwrapAdminData<{ success: true }>(response)));
  }

  connectClientPms(clientId: string, payload: AdminClientPmsConnectPayload) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/pms/connect`, payload)
      .pipe(map((response) => unwrapAdminData<AdminPmsConnectionStatusModel>(response)));
  }

  getClientPriceLabsStatus(clientId: string) {
    return this.http
      .get<unknown>(`${this.baseUrl}/clients/${clientId}/integrations/pricelabs/status`)
      .pipe(map((response) => unwrapAdminData<PriceLabsConnectionStatus>(response)));
  }

  setClientPropertyPriceLabsEnabled(clientId: string, propertyId: string, enabled: boolean) {
    return this.http
      .patch<unknown>(
        `${this.baseUrl}/clients/${clientId}/integrations/pricelabs/properties/${propertyId}/enabled`,
        { enabled }
      )
      .pipe(map((response) => unwrapAdminData<PriceLabsPropertyMapping>(response)));
  }

  getClientOwnerRezWebhookAuth(clientId: string) {
    return this.http
      .get<unknown>(`${this.baseUrl}/clients/${clientId}/pms/ownerrez/webhook-auth`)
      .pipe(map((response) => unwrapAdminData<OwnerRezWebhookAuthStatusModel>(response)));
  }

  updateClientOwnerRezWebhookAuth(clientId: string, payload: UpdateOwnerRezWebhookAuthPayload) {
    return this.http
      .put<unknown>(`${this.baseUrl}/clients/${clientId}/pms/ownerrez/webhook-auth`, payload)
      .pipe(map((response) => unwrapAdminData<OwnerRezWebhookAuthStatusModel>(response)));
  }

  resendClientOwnerRezAuthorizationEmail(clientId: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/pms/ownerrez/reauthorization-email`, {})
      .pipe(map((response) => unwrapAdminData<OwnerRezWebhookAuthStatusModel>(response)));
  }

  validateClientOwnerRezConnection(clientId: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/pms/ownerrez/validate`, {})
      .pipe(map((response) => unwrapAdminData<OwnerRezAdminValidationResultModel>(response)));
  }

  getClientPmsStatus(clientId: string, returnUrl?: string) {
    let params = new HttpParams();
    if (returnUrl) {
      params = params.set('returnUrl', returnUrl);
    }

    return this.http
      .get<unknown>(`${this.baseUrl}/clients/${clientId}/pms/status`, { params })
      .pipe(map((response) => unwrapAdminArray<AdminPmsConnectionStatusModel>(response)));
  }

  getClientRemotePmsProperties(clientId: string) {
    return this.http
      .get<unknown>(`${this.baseUrl}/clients/${clientId}/pms/remote-properties`)
      .pipe(map((response) => unwrapAdminArray<AdminRemotePmsPropertyModel>(response)));
  }

  saveClientPropertyMappings(clientId: string, payload: AdminPropertyMappingRequest) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/pms/map-properties`, payload)
      .pipe(map((response) => unwrapAdminArray<AdminRemotePmsPropertyModel>(response)));
  }

  runClientInitialImport(clientId: string, entityType?: AdminInitialImportStatusModel['entityType']) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/pms/import`, { entityType })
      .pipe(map((response) => unwrapAdminArray<AdminInitialImportStatusModel>(response)));
  }

  getClientInitialImportDetails(
    clientId: string,
    entityType: AdminInitialImportStatusModel['entityType'],
    page = 1,
    pageSize = 25,
    providerId?: string,
    pmsConnectionId?: string,
    propertyId?: string
  ) {
    let params = new HttpParams()
      .set('entityType', entityType)
      .set('page', String(page))
      .set('pageSize', String(pageSize));

    if (providerId) {
      params = params.set('providerId', providerId);
    }

    if (pmsConnectionId) {
      params = params.set('pmsConnectionId', pmsConnectionId);
    }

    if (propertyId) {
      params = params.set('propertyId', propertyId);
    }

    return this.http
      .get<unknown>(`${this.baseUrl}/clients/${clientId}/pms/import/details`, { params })
      .pipe(map((response) => unwrapAdminData<AdminInitialImportDetailPageModel>(response)));
  }

  runClientPmsSync(clientId: string, providerId?: string, returnUrl?: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/pms/sync`, { providerId, returnUrl })
      .pipe(map((response) => unwrapAdminArray<AdminRemotePmsPropertyModel>(response)));
  }

  updateClientOnboardingStatus(clientId: string, status: string, note?: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/status`, { status, note })
      .pipe(map((response) => unwrapAdminData<ClientDetailModel>(response)));
  }

  completeClientOnboarding(clientId: string, note?: string) {
    return this.updateClientOnboardingStatus(clientId, 'Completed', note);
  }

  activateClientLive(clientId: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/activate-live`, {})
      .pipe(map((response) => unwrapAdminData<ClientDetailModel>(response)));
  }

  reopenClientOnboarding(clientId: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/reopen-onboarding`, {})
      .pipe(map((response) => unwrapAdminData<ClientDetailModel>(response)));
  }

  markClientSetupFailed(clientId: string, note?: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/mark-setup-failed`, { note })
      .pipe(map((response) => unwrapAdminData<ClientDetailModel>(response)));
  }

  suspendClient(clientId: string, note?: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/suspend`, { note })
      .pipe(map((response) => unwrapAdminData<ClientDetailModel>(response)));
  }

  updateClient(clientId: string, payload: AdminClientUpsertPayload) {
    return this.http
      .put<unknown>(`${this.baseUrl}/clients/${clientId}`, payload)
      .pipe(map((response) => unwrapAdminClients([unwrapAdminData(response)])[0]));
  }

  resendClientEmailConfirmation(clientId: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/resend-email-confirmation`, {})
      .pipe(map((response) => unwrapAdminData<AdminEmailConfirmationResendResponse>(response)));
  }

  generateClientEmailConfirmationLink(clientId: string, frontendBaseUrl: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/email-confirmation-link`, { frontendBaseUrl })
      .pipe(map((response) => unwrapAdminData<AdminEmailConfirmationLinkResponse>(response)));
  }

  deleteClient(clientId: string) {
    return this.http
      .delete<unknown>(`${this.baseUrl}/clients/${clientId}`)
      .pipe(map(() => ({ success: true as const })));
  }

  archiveClient(clientId: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/archive`, {})
      .pipe(map(() => ({ success: true as const })));
  }

  impersonateClient(clientId: string, returnUrl?: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/clients/${clientId}/impersonate`, {
        returnUrl
      })
      .pipe(map((response) => unwrapAdminData<AuthResponse>(response)));
  }

  getUsers() {
    return this.http
      .get<unknown>(`${this.baseUrl}/users`)
      .pipe(map((response) => unwrapAdminUsers(response)));
  }

  createUser(payload: AdminUserUpsertPayload) {
    return this.http
      .post<unknown>(`${this.baseUrl}/users`, payload)
      .pipe(map((response) => unwrapAdminData<AdminUserRecord>(response)));
  }

  updateUser(userId: string, payload: AdminUserUpsertPayload) {
    return this.http
      .put<unknown>(`${this.baseUrl}/users/${userId}`, payload)
      .pipe(map((response) => unwrapAdminData<AdminUserRecord>(response)));
  }

  deleteUser(userId: string) {
    return this.http
      .delete<unknown>(`${this.baseUrl}/users/${userId}`)
      .pipe(map((response) => unwrapAdminData<{ success: true }>(response)));
  }

  deactivateUser(userId: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/users/${userId}/deactivate`, {})
      .pipe(map((response) => unwrapAdminData<AdminUserRecord>(response)));
  }

  activateUser(userId: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/users/${userId}/activate`, {})
      .pipe(map((response) => unwrapAdminData<AdminUserRecord>(response)));
  }

  getSettings() {
    return this.http
      .get<unknown>(`${this.baseUrl}/settings`)
      .pipe(map((response) => unwrapAdminSettings(response)));
  }

  getNotificationOperations(query: AdminNotificationOperationsQuery = {}) {
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<AdminNotificationOperationsOverview>(`${this.baseUrl}/notification-operations`, { params });
  }

  replayNotification(notificationRequestId: string, reason: string, idempotencyKey: string) {
    return this.http.post<{ notificationRequestId: string; correlationId: string }>(
      `${this.baseUrl}/notifications/${notificationRequestId}/replay`,
      { reason, idempotencyKey }
    );
  }

  updateSettings(payload: Partial<AdminSettings>) {
    return this.http
      .put<unknown>(`${this.baseUrl}/settings`, payload)
      .pipe(map((response) => unwrapAdminSettings(response)));
  }

  getPayments(query: AdminPaymentQuery = {}) {
    let params = new HttpParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this.http
      .get<unknown>(`${this.baseUrl}/payments`, { params })
      .pipe(map((response) => unwrapAdminPayments(response)));
  }

  getPaymentChart(startDate: string, endDate: string) {
    return this.http
      .get<unknown>(`${this.baseUrl}/dashboard/payments-chart`, {
        params: new HttpParams().set('from', `${startDate}T00:00:00Z`).set('to', `${endDate}T23:59:59.9999999Z`).set('groupBy', 'day')
      })
      .pipe(map((response) => unwrapAdminPaymentChart(response)));
  }

  stopImpersonation(returnUrl?: string, refreshToken?: string | null) {
    return this.http
      .post<unknown>(`${this.baseUrl}/impersonation/stop`, {
        returnUrl,
        refreshToken
      })
      .pipe(map((response) => unwrapAdminData<AuthResponse>(response)));
  }
}
